/**
 * Rating-Ampel (Detailseite-Design): normalisiert einen Score auf 0–10 und
 * ordnet ihn ein — gut ≥7,0 (grün) · mittel 5,5–6,9 (gelb) · schwach <5,5 (rot).
 * Die Klassen-Maps enthalten die vollen Tailwind-Klassen als Literale, damit der
 * Content-Scanner sie findet (kein dynamischer Klassenbau).
 */
export type RatingTier = 'good' | 'mid' | 'bad'

export function ratingTier(value: number, max = 10): RatingTier {
	const normalized = (value / max) * 10
	if (normalized >= 7) return 'good'
	if (normalized >= 5.5) return 'mid'
	return 'bad'
}

/** Nur die Wertfarbe (z. B. für Listen-Badges). */
export const TIER_TEXT: Record<RatingTier, string> = {
	good: 'text-rating-good',
	mid: 'text-rating-mid',
	bad: 'text-rating-bad',
}

/** Getönter Chip (Value farbig, Hintergrund leicht eingefärbt). */
export const TIER_CHIP: Record<RatingTier, string> = {
	good: 'text-rating-good bg-rating-good/10',
	mid: 'text-rating-mid bg-rating-mid/10',
	bad: 'text-rating-bad bg-rating-bad/10',
}

/** Farbiger Seitenstreifen der Detailseiten-Rating-Karte. */
export const TIER_STRIPE: Record<RatingTier, string> = {
	good: 'bg-rating-good',
	mid: 'bg-rating-mid',
	bad: 'bg-rating-bad',
}

/** CSS-Variablenname der Ampelfarbe (für inline-Verläufe per color-mix). */
export const TIER_VAR: Record<RatingTier, string> = {
	good: '--color-rating-good',
	mid: '--color-rating-mid',
	bad: '--color-rating-bad',
}

/**
 * Bayesianisch gewichtete Bewertung (0–10).
 *
 * Ohne sie gewinnt in jeder Sortierung „nach Bewertung" der Titel mit 9,4 aus
 * 26 Stimmen gegen den mit 8,8 aus zwei Millionen — gemeldet aus „Entdecken",
 * wo genau so ein Ausreißer auf Platz 1 stand. Die Formel zieht wenige Stimmen
 * in Richtung des Mittelwerts: Erst wer genug Stimmen hat, darf ganz nach oben.
 *
 * `MIN_VOTES` ist die Stärke des Vorurteils, nicht eine Mindestgrenze — Titel
 * mit wenigen Stimmen verschwinden nicht, sie stehen nur nicht mehr vorn.
 */
export const RATING_PRIOR_VOTES = 1000
export const RATING_PRIOR_MEAN = 6.5

export function bayesianRating(
	rating: number | null | undefined,
	votes: number | null | undefined,
): number | null {
	if (rating == null || !votes || votes <= 0) return null
	const weight = votes / (votes + RATING_PRIOR_VOTES)
	return weight * rating + (1 - weight) * RATING_PRIOR_MEAN
}
